'use client';

/**
 * JourneyProgressRail — the persistent, journey-level progress indicator.
 *
 * One segmented bar, one chunk per member-facing section (form sections +
 * sensagrams), shown in the persistent TOP status band and visible across EVERY
 * phase (the caller positions it; this component is layout-agnostic). Overall
 * position reads directly from the bars: filled (done) sections on the left,
 * faint (upcoming) on the right, and the ONE section the member is currently in
 * wears a haloed core — a solid, taller bar ringed by a breathing accent halo.
 * Section names stay in the accessible progress description. Visible labels are
 * an explicit opt-in; when enabled, labels and bars share equal grid columns.
 * The unlabeled rail retains its time-weighted segments.
 *
 * 2026-06-19: replaced the sliding "you are here" dot. The dot centered itself
 * on the active section regardless of how far through it the member actually was,
 * so it read as a false "you're halfway" (members found it confusing — it could
 * be 25% or 75% but always looked like 50%). The honest signals are: how many
 * bars are filled (overall), the haloed bar (which section is live), and the
 * in-section {@link SectionProgressRing} near the content (how far through THIS
 * section — the FINE language). The rail stays the OVERALL language and no longer
 * implies a precise sub-section position.
 *
 * 2026-08-02: the live section was `bg-accent animate-pulse` — the SAME solid
 * teal as a completed section, oscillating opacity 1→.5. That inverted the
 * hierarchy: for most of its cycle the live bar rendered WEAKER than the done
 * bars behind it, so it read as a third tier of doneness rather than as a
 * location, and members missed it. Under `prefers-reduced-motion` it settled at
 * a static `opacity-60` teal — a dimmed done-bar, the worst reading of all.
 *
 * It now wears a haloed core, signalling on three channels instead of one:
 *   shape  — a concentric accent halo no other state has
 *   size   — 8px tall against the 5px of every other segment
 *   motion — the breathe moved OFF the bar and ONTO the halo
 * The core itself never animates, so the live section is never dimmer than a
 * completed one. Because only the halo breathes, `prefers-reduced-motion` stops
 * the movement and leaves all three static channels intact (the halo simply
 * rests at full strength) rather than degrading the signal.
 *
 * Presentational and dumb: `segments` and `marker` are derived upstream
 * (`deriveSurveySegments` / `deriveJourneyMarker`). Renders nothing for a
 * single-section survey (the caller may also suppress). The breathe is pure CSS
 * (stock Tailwind `animate-pulse`, opacity-only — no custom keyframe to ship, so
 * it animates identically in every app that consumes @core).
 */

import type { JourneyMarker, SurveySegment } from '../lib/sectionProgress';

export interface JourneyProgressRailProps {
  segments: SurveySegment[];
  marker: JourneyMarker;
  /** Opt-in presentation; consumers must enable it through explicit configuration. */
  showSectionLabels?: boolean;
}

export function JourneyProgressRail({
  segments,
  marker,
  showSectionLabels = false,
}: JourneyProgressRailProps) {
  if (segments.length <= 1) return null;

  const count = segments.length;
  const doneCount = segments.filter((s) => s.status === 'done').length;
  const currentIndex = segments.findIndex((s) => s.status === 'current');
  const position =
    marker.placement === 'end'
      ? count
      : currentIndex >= 0
        ? currentIndex + 1
        : Math.max(doneCount, 1);
  const activeTitle =
    marker.activeIndex >= 0 ? (segments[marker.activeIndex]?.title ?? null) : null;
  const valueText = `Section ${position} of ${count}${activeTitle ? `: ${activeTitle}` : ''}`;
  // Halo (and mark active) only while the member is actually IN a section —
  // resting on an interstitial (placement 'before') or complete ('end') haloes
  // nothing, so the rail never implies activity where there is none.
  const highlightIndex = marker.placement === 'on' ? marker.activeIndex : -1;
  const labeledGridStyle = {
    // Time-weighted columns can leave a label only a few pixels wide. The opt-in
    // labeled presentation uses equal columns so every title has room to wrap.
    gridTemplateColumns: `repeat(${count}, minmax(0.5rem, 1fr))`,
  };

  return (
    <div
      role="progressbar"
      aria-label="Overall progress"
      aria-valuemin={0}
      aria-valuemax={count}
      aria-valuenow={doneCount}
      aria-valuetext={valueText}
      data-marker-placement={marker.placement}
      data-active-index={marker.activeIndex}
      className="relative w-full"
    >
      {/* Fixed-height track. The live segment is 8px against the others' 5px, so
          without a pinned height this row would grow and shrink by 3px every
          time the marker moved on or off a section — nudging the sticky status
          band and everything under it. A fixed height + `items-center` keeps the
          row constant and centres both bar heights inside it.

          Fixed px, not `h-2`: the other bars are `h-[5px]`, so a rem-based height
          here would make the 8/5 size contrast drift with the root font-size (it
          renders 7px at the 14px root this app uses). The contrast between the
          live bar and the rest is the signal — it must not depend on that. */}
      <div
        className={`${showSectionLabels ? 'grid' : 'flex'} h-[8px] w-full items-center gap-1.5`}
        style={showSectionLabels ? labeledGridStyle : undefined}
      >
        {segments.map((segment, i) => {
          // Bar width is proportional to the section's estimated time: flex-grow
          // carries the weight and flex-basis 0 makes growth purely proportional
          // (no content to bias it). A min width keeps even the shortest section
          // visible. Missing weight → 1, i.e. equal widths (back-compat).
          const barStyle = {
            flexGrow: segment.weight ?? 1,
            flexBasis: 0,
            minWidth: '0.5rem',
          } as const;
          if (i === highlightIndex) {
            // The live section: solid core + breathing halo. The wrapper stays
            // the single flex child that carries `barStyle`, so the segment is
            // still one direct child of the track (weights and the `[data-active]`
            // count are asserted that way in surveyProgress.test.tsx).
            //
            // The halo sits 3px proud on every side. `gap-1.5` is rem-based and
            // renders 5.25px at this app's 14px root, so the halo reaches a
            // little over halfway across the gap and still clears its neighbour
            // by 2.25px (measured). The 3px it spills above and below the track
            // is absorbed by the status band's padding — nothing clips it.
            return (
              <div
                key={segment.nodeKey}
                data-active="true"
                style={barStyle}
                className="relative h-[8px]"
              >
                <div
                  aria-hidden="true"
                  className="absolute -inset-[3px] rounded-full bg-accent/40 animate-pulse motion-reduce:animate-none"
                />
                {/* `relative` lifts the core above the absolutely-positioned
                    halo. It carries no animation: the live segment must never
                    render dimmer than a completed one. */}
                <div className="relative h-full w-full rounded-full bg-accent" />
              </div>
            );
          }
          const bg =
            segment.status === 'done'
              ? 'bg-accent'
              : segment.status === 'current'
                ? 'bg-accent/30'
                : 'bg-[rgba(26,35,50,0.10)]';
          return (
            <div
              key={segment.nodeKey}
              style={barStyle}
              className={`h-[5px] rounded-full transition-colors duration-300 ease-out motion-reduce:transition-none ${bg}`}
            />
          );
        })}
      </div>
      {showSectionLabels && (
        <div
          aria-hidden="true"
          className="mt-2 grid gap-1.5 font-body text-xs leading-relaxed"
          style={labeledGridStyle}
          data-section-labels
        >
          {segments.map((segment, i) => (
            <span
              key={segment.nodeKey}
              className={`min-w-0 break-words [overflow-wrap:anywhere] ${i === highlightIndex ? 'font-semibold text-accent-dark' : 'text-text-secondary'}`}
            >
              {i + 1}. {segment.title?.trim() || `Section ${i + 1}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
